import React, {useEffect} from 'react';
    import {useApi} from "../../../providers/ApiProvider";
    import BusinessesModel from "../../../models/BusinessesModel";
    import {useParams} from "react-router-dom";
    import {useBreadcrumbs} from "../../../providers/BreadcrumbsProvider";
    import BusinessViewHeader from "./../BusinessViewHeader";
    import BusinessViewMenu from "../BusinessViewMenu";
    import {useTranslation} from "react-i18next";
    import {useTranslation as useMyTranslation} from "../../../providers/TranslationProvider";
    import Chart from "react-apexcharts";
    import ButtonWithLoading from "../../layout/ui/ButtonWithLoading";

    function BusinessesReviewSentiment() {
        const {language} = useMyTranslation();
        const {t} = useTranslation();
        const params = useParams();
        const apiContext = useApi();
        const businessesModel = new BusinessesModel(apiContext.api);
        const breadcrumbs = useBreadcrumbs();

        const [initialLoading, setInitialLoading] = React.useState(true);
        const [analyzing, setAnalyzing] = React.useState(false);
        const [sentimentData, setSentimentData] = React.useState(null);

        const setMetaData = (profile) => {
            if (profile !== false) {
                breadcrumbs.update({
                    title: profile.title,
                    breadcrumbs: [
                        {label: t('Businesses List'), url: '/businesses'},
                        {label: profile.title, url: '/businesses/' + params.locationId},
                        {label: t('Stimmungsanalyse')}
                    ]
                });
                document.title = t('Stimmungsanalyse') + ' | ' + profile.title + ' | LocalBoost';
            }
        }

        const onProfileRefresh = (profile) => {
            setMetaData(profile);
        }

        const loadSavedSentiment = () => {
            setInitialLoading(true);
            businessesModel.getSavedSentiment(params.locationId).then((response) => {
                if (response !== false && response.data) {
                    setSentimentData(response.data);
                }
            }).finally(() => {
                setInitialLoading(false);
            });
        }

        const runNewAnalysis = () => {
            setAnalyzing(true);
            businessesModel.analyzeReviewsSentiment(params.locationId).then((response) => {
                if (response !== false) {
                    setSentimentData(response);
                }
            }).finally(() => {
                setAnalyzing(false);
            });
        }

        useEffect(() => {
            businessesModel.view(params.locationId).then((response) => {
                if (response !== false) {
                    setMetaData(response.data);
                }
            });
            loadSavedSentiment();
        }, [language]);

        const getSentimentColor = (sentiment) => {
            if (sentiment === 'positiv') return 'success';
            if (sentiment === 'negativ') return 'danger';
            return 'warning';
        }

        const getSentimentIcon = (sentiment) => {
            if (sentiment === 'positiv') return 'mood-happy';
            if (sentiment === 'negativ') return 'mood-sad';
            return 'mood-neutral';
        }

        const formatDate = (dateStr) => {
            if (!dateStr) return '';
            try {
                const d = new Date(dateStr);
                return d.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'});
            } catch(e) { return dateStr; }
        }

        const getRatingDistributionChart = () => {
            if (!sentimentData) return null;
            const dist = sentimentData.ratingDistribution;
            return {
                options: {
                    chart: {type: 'bar', toolbar: {show: false}, fontFamily: 'inherit'},
                    plotOptions: {bar: {horizontal: false, columnWidth: '55%', borderRadius: 6, distributed: true}},
                    colors: ['#dc3545', '#fd7e14', '#ffc107', '#5D87FF', '#28a745'],
                    dataLabels: {enabled: true, style: {fontSize: '14px', fontWeight: 700, colors: ['#fff']}},
                    xaxis: {categories: ['1 \u2605', '2 \u2605', '3 \u2605', '4 \u2605', '5 \u2605'], labels: {style: {fontSize: '13px', fontWeight: 600}}},
                    yaxis: {labels: {style: {fontSize: '12px'}}},
                    legend: {show: false},
                    grid: {borderColor: '#f1f1f1'},
                    tooltip: {y: {formatter: (val) => val + ' ' + t('Bewertungen')}}
                },
                series: [{name: t('Bewertungen'), data: [dist['1'] || 0, dist['2'] || 0, dist['3'] || 0, dist['4'] || 0, dist['5'] || 0]}]
            };
        }

        const getMonthlyTrendsChart = () => {
            if (!sentimentData || !sentimentData.monthlyTrends?.length) return null;
            const trends = sentimentData.monthlyTrends;
            const months = trends.map(t => {
                const parts = t.month.split('-');
                if (parts.length === 2) {
                    const monthNames = ['Jan', 'Feb', 'M\u00e4r', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
                    return monthNames[parseInt(parts[1], 10) - 1] + ' ' + parts[0].slice(2);
                }
                return t.month;
            });
            return {
                options: {
                    chart: {
                        type: 'line',
                        toolbar: {show: false},
                        fontFamily: 'inherit',
                        zoom: {enabled: false},
                        dropShadow: {enabled: true, top: 3, left: 0, blur: 4, color: '#5D87FF', opacity: 0.15}
                    },
                    stroke: {curve: 'smooth', width: [0, 3], lineCap: 'round'},
                    fill: {
                        type: ['solid', 'gradient'],
                        opacity: [0.85, 1],
                        gradient: {shade: 'light', type: 'vertical', shadeIntensity: 0.3, opacityFrom: 0.9, opacityTo: 0.6}
                    },
                    plotOptions: {bar: {columnWidth: '45%', borderRadius: 5}},
                    markers: {size: [0, 5], colors: ['#fff'], strokeColors: '#13DEB9', strokeWidth: 2, hover: {sizeOffset: 2}},
                    xaxis: {
                        categories: months,
                        labels: {style: {fontSize: '12px', colors: '#a1aab2'}},
                        axisBorder: {show: false},
                        axisTicks: {show: false}
                    },
                    yaxis: [
                        {
                            title: {text: t('Anzahl'), style: {fontSize: '12px', color: '#a1aab2', fontWeight: 500}},
                            labels: {style: {fontSize: '12px', colors: '#a1aab2'}, formatter: (val) => Math.round(val).toString()}
                        },
                        {
                            opposite: true,
                            title: {text: t('\u00d8 Bewertung'), style: {fontSize: '12px', color: '#a1aab2', fontWeight: 500}},
                            min: 0, max: 5,
                            labels: {style: {fontSize: '12px', colors: '#a1aab2'}, formatter: (val) => val.toFixed(1)},
                            tickAmount: 5
                        }
                    ],
                    legend: {
                        position: 'top',
                        horizontalAlign: 'right',
                        fontSize: '13px',
                        fontWeight: 500,
                        markers: {width: 10, height: 10, radius: 3},
                        itemMargin: {horizontal: 12}
                    },
                    colors: ['#5D87FF', '#13DEB9'],
                    grid: {borderColor: '#f0f0f0', strokeDashArray: 3, xaxis: {lines: {show: false}}, padding: {top: -10}},
                    tooltip: {
                        shared: true,
                        intersect: false,
                        theme: 'light',
                        y: {formatter: (val, opts) => {
                            if (opts.seriesIndex === 1) return val?.toFixed(1) + ' \u2605';
                            return val + ' ' + t('Bewertungen');
                        }}
                    },
                    dataLabels: {enabled: false}
                },
                series: [
                    {name: t('Anzahl Bewertungen'), type: 'column', data: trends.map(t => t.count)},
                    {name: t('\u00d8 Bewertung'), type: 'line', data: trends.map(t => t.avgRating)}
                ]
            };
        }

        const ratingChart = getRatingDistributionChart();
        const monthlyChart = getMonthlyTrendsChart();
        const ai = sentimentData?.aiAnalysis;

        const kpiCards = sentimentData ? [
            {icon: 'star-filled', color: 'warning', value: sentimentData.avgRating, label: t('Durchschnittsbewertung')},
            {icon: 'message-2', color: 'primary', value: sentimentData.totalReviews, label: t('Bewertungen gesamt')},
            {icon: 'messages', color: 'success', value: sentimentData.responseRate + '%', label: t('Antwortrate')},
            {icon: ai ? getSentimentIcon(ai.overallSentiment) : 'mood-neutral', color: ai ? getSentimentColor(ai.overallSentiment) : 'secondary', value: ai?.overallSentiment || '-', label: t('Gesamtstimmung')},
        ] : [];

        const kpiCards2 = sentimentData ? [
            {icon: 'gauge', color: 'info', value: ai?.sentimentScore != null ? (ai.sentimentScore > 0 ? '+' : '') + (typeof ai.sentimentScore === 'number' ? ai.sentimentScore.toFixed(2) : ai.sentimentScore) : '-', label: t('Stimmungswert')},
            {icon: 'message-reply', color: 'cyan', value: sentimentData.respondedCount ?? '-', label: t('Beantwortete Bewertungen')},
            {icon: 'percentage', color: 'violet', value: sentimentData.totalReviews > 0 ? Math.round((sentimentData.ratingDistribution?.['5'] || 0) / sentimentData.totalReviews * 100) + '%' : '0%', label: t('5-Sterne-Anteil')},
        ] : [];

        return (
            <>
                <BusinessViewHeader locationId={params.locationId} onProfileRefresh={onProfileRefresh} />
                <BusinessViewMenu locationId={params.locationId} active="reviews" />

                {initialLoading && (
                    <div className="card border-0 shadow-sm mt-3">
                        <div className="card-body p-5 text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted mb-0">{t('Daten werden geladen...')}</p>
                        </div>
                    </div>
                )}

                {!initialLoading && !sentimentData && !analyzing && (
                    <div className="card border-0 shadow-sm mt-3">
                        <div className="card-body p-5 text-center">
                            <div className="rounded-circle bg-light-primary d-inline-flex align-items-center justify-content-center mb-3" style={{width: '72px', height: '72px'}}>
                                <i className="ti ti-chart-dots-3 fs-1 text-primary"></i>
                            </div>
                            <h5 className="fw-semibold">{t('Bewertungs-Stimmungsanalyse')}</h5>
                            <p className="text-muted mb-4 mx-auto" style={{maxWidth: '500px'}}>{t('Lassen Sie Ihre Bewertungen von unserer KI analysieren. Erfahren Sie, welche Themen h\u00e4ufig erw\u00e4hnt werden, was gelobt und was kritisiert wird.')}</p>
                            <ButtonWithLoading className="btn-primary btn-lg" noOriginalIconClassName={true} iconClassName="ti ti-sparkles me-2" label={t('Analyse starten')} loading={analyzing} onClick={runNewAnalysis} />
                        </div>
                    </div>
                )}

                {analyzing && (
                    <div className="card border-0 shadow-sm mt-3">
                        <div className="card-body p-5 text-center">
                            <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <h5 className="fw-semibold">{t('KI-Analyse l\u00e4uft...')}</h5>
                            <p className="text-muted mb-0">{t('Ihre Bewertungen werden von der KI analysiert. Dies kann einige Sekunden dauern.')}</p>
                        </div>
                    </div>
                )}

                {sentimentData && !analyzing && (
                    <>
                        {sentimentData.analyzedAt && (
                            <div className="card border-0 shadow-sm mt-3 mb-3">
                                <div className="card-body py-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-muted fs-3">
                                            <i className="ti ti-clock me-1"></i>
                                            {t('Letzte Analyse')}: {formatDate(sentimentData.analyzedAt)}
                                        </span>
                                        <ButtonWithLoading className="btn-outline-primary btn-sm" noOriginalIconClassName={true} iconClassName="ti ti-refresh me-2" label={t('Analyse aktualisieren')} loading={analyzing} onClick={runNewAnalysis} />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="row mb-3">
                            {kpiCards.map((card, idx) => (
                                <div className="col-sm-6 col-xl-3 mb-3" key={'kpi-' + idx}>
                                    <div className="card border-0 shadow-sm h-100 mb-0">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <div className={'rounded-circle d-flex align-items-center justify-content-center bg-light-' + card.color} style={{width: '48px', height: '48px'}}>
                                                    <i className={'ti ti-' + card.icon + ' fs-6 text-' + card.color}></i>
                                                </div>
                                            </div>
                                            <h3 className="fw-semibold mb-1">{card.value}</h3>
                                            <p className="text-muted mb-0 fs-3">{card.label}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="row mb-3">
                            {kpiCards2.map((card, idx) => (
                                <div className="col-sm-6 col-xl-4 mb-3" key={'kpi2-' + idx}>
                                    <div className="card border-0 shadow-sm h-100 mb-0">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <div className={'rounded-circle d-flex align-items-center justify-content-center bg-light-' + card.color} style={{width: '48px', height: '48px'}}>
                                                    <i className={'ti ti-' + card.icon + ' fs-6 text-' + card.color}></i>
                                                </div>
                                            </div>
                                            <h3 className="fw-semibold mb-1">{card.value}</h3>
                                            <p className="text-muted mb-0 fs-3">{card.label}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="row mb-3">
                            <div className="col-lg-5">
                                <div className="card border-0 shadow-sm h-100 mb-0">
                                    <div className="card-body p-4">
                                        <h6 className="fw-semibold mb-3"><i className="ti ti-chart-bar me-2 text-primary"></i>{t('Bewertungsverteilung')}</h6>
                                        {ratingChart && <Chart type="bar" options={ratingChart.options} series={ratingChart.series} height={280} />}
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="card border-0 shadow-sm h-100 mb-0">
                                    <div className="card-body p-4">
                                        <h6 className="fw-semibold mb-3"><i className="ti ti-trending-up me-2 text-success"></i>{t('Monatlicher Verlauf')}</h6>
                                        {monthlyChart ? <Chart type="line" options={monthlyChart.options} series={monthlyChart.series} height={320} /> : <p className="text-muted text-center py-4">{t('Nicht gen\u00fcgend Daten f\u00fcr den monatlichen Verlauf.')}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {ai && (
                            <>
                                <div className="card border-0 shadow-sm mb-3" style={{borderLeft: '4px solid var(--bs-primary)', borderLeftStyle: 'solid'}}>
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <div className="rounded-circle bg-light-primary d-flex align-items-center justify-content-center me-3" style={{width: '40px', height: '40px'}}>
                                                <i className="ti ti-robot fs-6 text-primary"></i>
                                            </div>
                                            <h6 className="fw-semibold mb-0">{t('KI-Zusammenfassung')}</h6>
                                        </div>
                                        <p className="mb-0 fs-3" style={{lineHeight: '1.7'}}>{ai.summary}</p>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-lg-6 mb-3 mb-lg-0">
                                        <div className="card border-0 shadow-sm h-100 mb-0" style={{borderTop: '3px solid var(--bs-success)'}}>
                                            <div className="card-body p-4">
                                                <div className="d-flex align-items-center mb-3">
                                                    <div className="rounded-circle bg-light-success d-flex align-items-center justify-content-center me-3" style={{width: '40px', height: '40px'}}>
                                                        <i className="ti ti-thumb-up fs-6 text-success"></i>
                                                    </div>
                                                    <h6 className="fw-semibold mb-0">{t('H\u00e4ufig gelobt')}</h6>
                                                </div>
                                                {(ai.topPositiveTopics || []).length > 0 ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-sm align-middle mb-0">
                                                            <thead><tr className="border-bottom"><th className="text-muted fw-semibold fs-2 py-2">{t('Thema')}</th><th className="text-center text-muted fw-semibold fs-2 py-2">{t('Erw\u00e4hnungen')}</th><th className="text-muted fw-semibold fs-2 py-2">{t('Beispiel')}</th></tr></thead>
                                                            <tbody>
                                                                {ai.topPositiveTopics.map((topic, i) => (
                                                                    <tr key={'pos-' + i}>
                                                                        <td><span className="badge bg-light-success text-success rounded-pill px-3">{topic.topic}</span></td>
                                                                        <td className="text-center fw-semibold">{topic.mentions}</td>
                                                                        <td className="fs-2 text-muted fst-italic">\u201e{topic.example}\u201c</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : <p className="text-muted mb-0">{t('Keine positiven Themen erkannt.')}</p>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-6">
                                        <div className="card border-0 shadow-sm h-100 mb-0" style={{borderTop: '3px solid var(--bs-danger)'}}>
                                            <div className="card-body p-4">
                                                <div className="d-flex align-items-center mb-3">
                                                    <div className="rounded-circle bg-light-danger d-flex align-items-center justify-content-center me-3" style={{width: '40px', height: '40px'}}>
                                                        <i className="ti ti-thumb-down fs-6 text-danger"></i>
                                                    </div>
                                                    <h6 className="fw-semibold mb-0">{t('H\u00e4ufig kritisiert')}</h6>
                                                </div>
                                                {(ai.topNegativeTopics || []).length > 0 ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-sm align-middle mb-0">
                                                            <thead><tr className="border-bottom"><th className="text-muted fw-semibold fs-2 py-2">{t('Thema')}</th><th className="text-center text-muted fw-semibold fs-2 py-2">{t('Erw\u00e4hnungen')}</th><th className="text-muted fw-semibold fs-2 py-2">{t('Beispiel')}</th></tr></thead>
                                                            <tbody>
                                                                {ai.topNegativeTopics.map((topic, i) => (
                                                                    <tr key={'neg-' + i}>
                                                                        <td><span className="badge bg-light-danger text-danger rounded-pill px-3">{topic.topic}</span></td>
                                                                        <td className="text-center fw-semibold">{topic.mentions}</td>
                                                                        <td className="fs-2 text-muted fst-italic">\u201e{topic.example}\u201c</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : <p className="text-muted mb-0">{t('Keine negativen Themen erkannt.')}</p>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {(ai.keywords || []).length > 0 && (
                                    <div className="card border-0 shadow-sm mb-3">
                                        <div className="card-body p-4">
                                            <div className="d-flex align-items-center mb-3">
                                                <div className="rounded-circle bg-light-info d-flex align-items-center justify-content-center me-3" style={{width: '40px', height: '40px'}}>
                                                    <i className="ti ti-tags fs-6 text-info"></i>
                                                </div>
                                                <h6 className="fw-semibold mb-0">{t('Schl\u00fcsselw\u00f6rter')}</h6>
                                            </div>
                                            <div className="d-flex flex-wrap gap-2">
                                                {ai.keywords.map((kw, i) => {
                                                    const color = kw.sentiment === 'positiv' ? 'success' : kw.sentiment === 'negativ' ? 'danger' : 'secondary';
                                                    return (
                                                        <span key={'kw-' + i} className={"badge bg-light-" + color + " text-" + color + " rounded-pill px-3 py-2 fs-3"}>
                                                            {kw.word} <span className="ms-1 opacity-75">({kw.count})</span>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {(ai.actionItems || []).length > 0 && (
                                    <div className="card border-0 shadow-sm mb-3" style={{borderTop: '3px solid var(--bs-warning)'}}>
                                        <div className="card-body p-4">
                                            <div className="d-flex align-items-center mb-3">
                                                <div className="rounded-circle bg-light-warning d-flex align-items-center justify-content-center me-3" style={{width: '40px', height: '40px'}}>
                                                    <i className="ti ti-bulb fs-6 text-warning"></i>
                                                </div>
                                                <h6 className="fw-semibold mb-0">{t('Empfohlene Ma\u00dfnahmen')}</h6>
                                            </div>
                                            <ol className="mb-0 ps-3">
                                                {ai.actionItems.map((item, i) => <li key={'action-' + i} className="mb-2 fs-3">{item}</li>)}
                                            </ol>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {!sentimentData.analyzedAt && (
                            <div className="text-center mt-3 mb-4">
                                <ButtonWithLoading className="btn-outline-primary" noOriginalIconClassName={true} iconClassName="ti ti-refresh me-2" label={t('Analyse aktualisieren')} loading={analyzing} onClick={runNewAnalysis} />
                            </div>
                        )}
                    </>
                )}
            </>
        );
    }

    export default BusinessesReviewSentiment;
  